import React, {useMemo, useState} from 'react';
import {view, Icon, tokens} from '@sail/ui';
import type {FormatXMLElementFn} from '@sail/intl';
import {
  useSelector,
  setValue,
  resetAnyCryptoRefundState,
} from 'src/v2/state/store';
import {Text} from 'src/v2/components/common/Text';
import {Link} from 'src/v2/components/common/Link';
import {SelectionCard} from 'src/v2/components/common/SelectionCard';
import {ScreenContentLayout} from 'src/v2/components/screen/ScreenContentLayout';
import {
  TOKEN_INFO_MAP,
  type TokenSelectCurrency,
} from 'src/v2/components/anyCrypto/tokenSelect/tokenSelectConstants';
import {getCurrencyIcon} from 'src/v2/util/getCurrencyIcon';
import {formatAssetCode} from 'src/v2/util/formatAssetCode';
import {getNetworkName} from 'src/components/Wallet/constants';
import type {Screen, Wallet} from 'src/v2/state/types';
import {setUrlParam} from 'src/utils/utils';
import {useWalletConnectionClient} from 'src/v2/state/walletConnection';
import {
  findCompatibleWallet,
  getTokenNetworkTypes,
} from 'src/v2/util/walletCompatibility';

import {message, useIntl} from '@sail/intl';

const messages = {
  selectToken: message({
    id: 'crypto.payins.v2.token_select.select_token',
    description: 'Label for token selection list',
    defaultMessage: 'Select token',
  }),

  networkSublabel: message<{
    networks: string;
  }>({
    id: 'crypto.payins.v2.token_select.network_sublabel',
    description: 'Sublabel showing which networks a token is available on',
    defaultMessage: 'on {networks}',
  }),

  relayTerms: message<{
    a: FormatXMLElementFn;
  }>({
    id: 'crypto.payins.v2.any_crypto.deposit.relay_terms',
    description: 'Relay terms disclosure',
    defaultMessage:
      'Digital asset conversion services provided by third parties, subject to <a>Terms</a>.',
  }),
};

export const TokenSelectScreen = () => {
  const intl = useIntl();
  const walletClient = useWalletConnectionClient();
  const supportedCurrencies = useSelector((state) => state.supportedCurrencies);
  const [isContinuing, setIsContinuing] = useState(false);

  const tokenList = useMemo(() => {
    const supportedAssets = new Set(
      supportedCurrencies.map((c) => c.assetCode),
    );
    return (Object.keys(TOKEN_INFO_MAP) as TokenSelectCurrency[]).filter(
      (token) => supportedAssets.has(token),
    );
  }, [supportedCurrencies]);

  const [selectedToken, setSelectedToken] =
    useState<TokenSelectCurrency | null>(null);

  const getNetworkSublabel = (token: string) => {
    const networks = supportedCurrencies
      .filter((c) => c.assetCode === token)
      .map((c) => getNetworkName(c.currencyNetwork));

    if (networks.length === 0) return '';
    const networkList = intl.formatList(networks, {type: 'conjunction'});
    return intl.formatMessage(messages.networkSublabel, {
      networks: networkList,
    });
  };

  const activeWallet = useSelector(
    (state) => state.walletConnection.activeWallet,
  );
  const connectedWallets = useSelector(
    (state) => state.walletConnection.connectedWallets,
  );

  const walletConnectEnabled = useSelector(
    (state) => state.featureFlags.enable_pay_with_any_crypto_wallet_connect,
  );
  const walletConnectSupportedCurrencies = useSelector(
    (state) => state.walletConnectSupportedCurrencies,
  );
  const isRelayFlow = useSelector(
    (state) => state.featureFlags.enable_pay_with_any_crypto,
  );

  const setSelectedTokenUrlParams = (token: TokenSelectCurrency) => {
    setUrlParam('selectedToken', token, walletClient);
    setUrlParam('selectedWallet', null, walletClient);
  };

  const handleWalletConnectSelect = async (token: TokenSelectCurrency) => {
    if (isContinuing) {
      return;
    }

    setSelectedTokenUrlParams(token);

    // Route to the pay screen only when a connected wallet can actually pay the
    // token's network (e.g. an EVM wallet for ETH/USDT, a Solana wallet for
    // SOL). Otherwise send the user to the grid to connect a compatible wallet.
    const tokenNetworkTypes = getTokenNetworkTypes(
      token,
      walletConnectSupportedCurrencies,
    );
    const compatibleWallet = findCompatibleWallet(
      activeWallet,
      connectedWallets,
      tokenNetworkTypes,
    );

    let nextScreen: Screen = 'WALLET_SELECT';
    let nextActiveWallet: Wallet | null = null;

    if (compatibleWallet) {
      try {
        if (activeWallet?.id !== compatibleWallet.id) {
          setIsContinuing(true);
          await walletClient.switchWallet(compatibleWallet.id);
        }

        nextActiveWallet = compatibleWallet;
        nextScreen = 'PAY';
      } catch {
        // Swallow the switch error and fall back to the wallet select grid.
      } finally {
        setIsContinuing(false);
      }
    }

    setValue((state) => {
      state.anyCryptoDeposit.selectedToken = token;
      // Clear the previously selected currency and its quote so the pay screen
      // doesn't fire a quote request for (or briefly display) the old token
      // before it syncs to the newly selected one.
      state.selectedCurrency = null;
      state.anyCryptoDeposit.quote = {
        depositAddress: null,
        inputAmount: null,
        inputChain: null,
        inputCurrency: null,
      };
      state.screen = nextScreen;

      if (nextActiveWallet) {
        state.walletConnection.activeWallet = nextActiveWallet;
      }
    });
  };

  const handleDepositOnlySelect = (token: TokenSelectCurrency) => {
    setSelectedTokenUrlParams(token);

    const networks = supportedCurrencies
      .filter((c) => c.assetCode === token)
      .map((c) => c.currencyNetwork);

    if (networks.length > 1) {
      setValue((state) => {
        state.anyCryptoDeposit.selectedToken = token;
        state.anyCryptoDeposit.selectedNetwork = null;
        state.screen = 'ANY_CRYPTO_NETWORK_SELECT';
      });
    } else {
      setValue((state) => {
        state.anyCryptoDeposit.selectedToken = token;
        const [network] = networks;
        state.anyCryptoDeposit.selectedNetwork = network;
        resetAnyCryptoRefundState(state);
        if (network === 'bitcoin') {
          state.anyCryptoDeposit.refundMethod = 'setNewRefundAddress';
        }
        state.anyCryptoDeposit.quote = {
          depositAddress: null,
          inputAmount: null,
          inputChain: null,
          inputCurrency: null,
        };
        state.screen = 'ANY_CRYPTO_REFUND';
      });
    }
  };

  const handleTokenSelect = async (token: TokenSelectCurrency) => {
    setSelectedToken(token);

    const tokenSupportsWalletConnect =
      walletConnectEnabled &&
      walletConnectSupportedCurrencies.some((c) => c.assetCode === token);

    if (tokenSupportsWalletConnect) {
      await handleWalletConnectSelect(token);
    } else {
      handleDepositOnlySelect(token);
    }
  };

  const handleBack = isRelayFlow
    ? undefined
    : () =>
        setValue((state) => {
          state.screen = 'WALLET_SELECT';
        });

  return (
    <ScreenContentLayout onBack={handleBack}>
      <view.div css={{stack: 'y', gap: 'large'}}>
        <view.div css={{stack: 'y', gap: 'small'}}>
          <Text type="bodyEmphasized" css={{color: 'text'}}>
            {intl.formatMessage(messages.selectToken)}
          </Text>
          <view.div css={{stack: 'y', gap: 'small'}} role="listbox">
            {tokenList.map((token) => {
              const {processingTimeMessage} = TOKEN_INFO_MAP[token];
              return (
                <SelectionCard
                  key={token}
                  icon={
                    <Icon
                      icon={getCurrencyIcon(token)}
                      css={{
                        width: 'xlarge',
                        height: 'xlarge',
                        borderRadius: '50%',
                      }}
                    />
                  }
                  label={formatAssetCode(token)}
                  sublabel={getNetworkSublabel(token)}
                  accessory={intl.formatMessage(processingTimeMessage)}
                  selected={selectedToken === token}
                  disabled={isContinuing}
                  onSelect={() => handleTokenSelect(token)}
                />
              );
            })}
          </view.div>
        </view.div>
        {isRelayFlow ? (
          <view.div
            css={{
              font: 'label.small',
              color: tokens.color.neutral[500].toString(),
              textAlign: 'center',
            }}
          >
            {intl.formatMessage(messages.relayTerms, {
              a: (chunks: React.ReactNode) => (
                <Link
                  key="relay-terms-link"
                  href="https://relay.link/terms"
                  target="_blank"
                  type="secondary"
                >
                  {chunks}
                </Link>
              ),
            })}
          </view.div>
        ) : null}
      </view.div>
    </ScreenContentLayout>
  );
};
